import React, { useMemo, useState } from 'react';
import { copyToClipboard, useAdmin } from '../context';
import { ALL_CAPS, CapId, StaffState } from '../types';
import { COLOR_RE, DISCORD_ID_RE, sameSet, stripMember, stripRole } from './helpers';

// Export and import of the register. Export is a text area to copy from: the in-game browser has no
// download manager, so a "download" link would silently do nothing there. Import is a paste area with a
// preview of what would change before anything is sent.

interface ParsedImport {
  fatal: string | null;
  roles: Array<Record<string, unknown>>;
  members: Array<Record<string, unknown>>;
  lines: Array<{ kind: 'add' | 'update' | 'same' | 'error'; text: string }>;
  counts: { add: number; update: number; same: number; error: number };
}

const GRANTABLE: CapId[] = ALL_CAPS.filter((c) => c !== 'staff');

const asCaps = (v: unknown): { caps: CapId[]; dropped: string[] } => {
  const caps: CapId[] = [];
  const dropped: string[] = [];
  if (Array.isArray(v)) {
    for (const c of v) {
      if (GRANTABLE.indexOf(c as CapId) !== -1) {
        if (caps.indexOf(c as CapId) === -1) caps.push(c as CapId);
      } else dropped.push(String(c));
    }
  }
  return { caps, dropped };
};

export const parseImport = (raw: string, staff: StaffState): ParsedImport => {
  const out: ParsedImport = { fatal: null, roles: [], members: [], lines: [], counts: { add: 0, update: 0, same: 0, error: 0 } };
  let data: any;
  try {
    data = JSON.parse(raw);
  } catch (e) {
    out.fatal = "Ce texte n'est pas du JSON valide.";
    return out;
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    out.fatal = 'Le JSON doit être un objet avec les listes "roles" et "members".';
    return out;
  }
  const roles: any[] = Array.isArray(data.roles) ? data.roles : [];
  const members: any[] = Array.isArray(data.members) ? data.members : [];
  if (!roles.length && !members.length) {
    out.fatal = 'Aucun rôle ni membre à importer.';
    return out;
  }
  const add = (kind: 'add' | 'update' | 'same' | 'error', text: string) => {
    out.lines.push({ kind, text });
    out.counts[kind]++;
  };

  const roleIds: string[] = staff.roles.map((r) => r.id);
  for (const r of roles) {
    if (!r || typeof r !== 'object' || typeof r.name !== 'string' || !r.name.trim()) {
      add('error', 'Rôle ignoré : il faut un nom.');
      continue;
    }
    const { caps, dropped } = asCaps(r.caps);
    if (r.color !== undefined && !COLOR_RE.test(String(r.color))) {
      add('error', `Rôle ${r.name} ignoré : couleur invalide (attendu #rrggbb).`);
      continue;
    }
    const entry: Record<string, unknown> = { name: r.name.trim().slice(0, 32), description: typeof r.description === 'string' ? r.description.slice(0, 120) : '', color: r.color || '#c9a24f', caps };
    const existing = typeof r.id === 'string' ? staff.roles.find((x) => x.id === r.id) : undefined;
    if (typeof r.id === 'string' && r.id) {
      entry.id = r.id;
      if (roleIds.indexOf(r.id) === -1) roleIds.push(r.id);
    }
    out.roles.push(entry);
    const note = dropped.length ? ` (permissions ignorées : ${dropped.join(', ')})` : '';
    if (!existing) add('add', `Rôle à créer : ${entry.name}${note}`);
    else if (existing.name === entry.name && existing.description === entry.description && existing.color.toLowerCase() === String(entry.color).toLowerCase() && sameSet(existing.caps, caps)) add('same', `Rôle identique : ${entry.name}`);
    else add('update', `Rôle à modifier : ${existing.name}${note}`);
  }

  const seen: Record<string, boolean> = {};
  for (const m of members) {
    const id = m && typeof m.discordId === 'string' ? m.discordId.trim() : '';
    if (!DISCORD_ID_RE.test(id)) {
      add('error', `Membre ignoré : identifiant Discord invalide (${id || 'vide'}).`);
      continue;
    }
    if (seen[id]) {
      add('error', `Membre ${id} ignoré : présent deux fois.`);
      continue;
    }
    seen[id] = true;
    if (m.roleId && roleIds.indexOf(String(m.roleId)) === -1) {
      add('error', `Membre ${m.name || id} ignoré : rôle inconnu (${m.roleId}).`);
      continue;
    }
    const c = asCaps(m.caps);
    const d = asCaps(m.denied);
    const expiresAt = typeof m.expiresAt === 'number' && Number.isFinite(m.expiresAt) ? m.expiresAt : null;
    const entry: Record<string, unknown> = {
      discordId: id,
      name: typeof m.name === 'string' ? m.name.trim().slice(0, 48) : '',
      roleId: m.roleId ? String(m.roleId) : null,
      caps: c.caps,
      denied: d.caps,
      note: typeof m.note === 'string' ? m.note.slice(0, 160) : '',
      active: m.active !== false,
      expiresAt,
    };
    out.members.push(entry);
    const label = (entry.name as string) || id;
    const existing = staff.members.find((x) => x.discordId === id);
    if (!existing) add('add', `Membre à ajouter : ${label}`);
    else {
      const e = stripMember(existing) as any;
      const same = e.name === entry.name && e.roleId === entry.roleId && e.note === entry.note && e.active === entry.active && e.expiresAt === entry.expiresAt && sameSet(e.caps, c.caps) && sameSet(e.denied, d.caps);
      add(same ? 'same' : 'update', same ? `Membre identique : ${label}` : `Membre à modifier : ${label}`);
    }
  }
  return out;
};

const KIND_LABEL = { add: 'Ajout', update: 'Modification', same: 'Identique', error: 'Erreur' };

export const ToolsTab: React.FC<{ staff: StaffState }> = ({ staff }) => {
  const { act, confirm, toast } = useAdmin();
  const [raw, setRaw] = useState('');

  const exportText = useMemo(
    () => JSON.stringify({ version: 1, exportedAt: new Date().toISOString(), roles: staff.roles.map(stripRole), members: staff.members.map(stripMember) }, null, 2),
    [staff]
  );

  const parsed = useMemo(() => (raw.trim() ? parseImport(raw, staff) : null), [raw, staff]);
  const changes = parsed ? parsed.counts.add + parsed.counts.update : 0;

  const doImport = () => {
    if (!parsed || parsed.fatal || changes === 0) return;
    confirm({
      title: 'Importer dans le registre du staff ?',
      body: `${parsed.counts.add} ajout(s) et ${parsed.counts.update} modification(s). Les entrées en erreur sont ignorées. Les propriétaires ne sont jamais touchés.`,
      confirmLabel: 'Importer',
      onConfirm: () => {
        act('adminStaffImport', { roles: parsed.roles, members: parsed.members }, 'Import du registre');
        setRaw('');
      },
    });
  };

  return (
    <div className="admin-panel__toolstab">
      <section className="admin-panel__tool" aria-label="Export">
        <h3 className="admin-panel__tool-title">Exporter le registre</h3>
        <p className="admin-panel__hint">Rôles et membres au format JSON, sans les calculs du serveur. Copiez le texte pour le sauvegarder ou l'importer sur un autre serveur.</p>
        <textarea className="admin-panel__input admin-panel__input--block admin-panel__code" readOnly value={exportText} aria-label="Registre du staff au format JSON" onFocus={(e) => e.currentTarget.select()} />
        <div className="admin-panel__btnrow">
          <button
            type="button"
            className="admin-panel__button"
            onClick={() => {
              const ok = copyToClipboard(exportText);
              toast(ok ? 'Registre copié.' : 'Copie impossible : sélectionnez le texte et copiez-le à la main.', { kind: ok ? 'ok' : 'err' });
            }}
          >
            Copier le JSON
          </button>
          <span className="admin-panel__cell--muted">{staff.roles.length} rôles, {staff.members.length} membres</span>
        </div>
      </section>

      <section className="admin-panel__tool" aria-label="Import">
        <h3 className="admin-panel__tool-title">Importer un registre</h3>
        <p className="admin-panel__hint">Collez un export. L'import fusionne : il ajoute et modifie, il ne supprime jamais. Rien n'est envoyé avant votre confirmation.</p>
        <textarea className="admin-panel__input admin-panel__input--block admin-panel__code" value={raw} onChange={(e) => setRaw(e.target.value)} placeholder='{ "roles": [...], "members": [...] }' aria-label="JSON à importer" />
        {parsed ? (
          parsed.fatal ? (
            <p className="admin-panel__hint admin-panel__hint--warn">{parsed.fatal}</p>
          ) : (
            <>
              <div className="admin-panel__importsum" role="status">
                <span className="admin-panel__tag">{parsed.counts.add} ajout{parsed.counts.add > 1 ? 's' : ''}</span>
                <span className="admin-panel__tag">{parsed.counts.update} modification{parsed.counts.update > 1 ? 's' : ''}</span>
                <span className="admin-panel__tag">{parsed.counts.same} identique{parsed.counts.same > 1 ? 's' : ''}</span>
                {parsed.counts.error ? <span className="admin-panel__tag admin-panel__tag--danger">{parsed.counts.error} erreur{parsed.counts.error > 1 ? 's' : ''}</span> : null}
              </div>
              <ul className="admin-panel__importlist">
                {parsed.lines.slice(0, 80).map((l, i) => (
                  <li key={i} className={'admin-panel__importline admin-panel__importline--' + l.kind}>
                    <span className="admin-panel__importkind">{KIND_LABEL[l.kind]}</span>
                    <span>{l.text}</span>
                  </li>
                ))}
              </ul>
              {parsed.lines.length > 80 ? <p className="admin-panel__hint">Et {parsed.lines.length - 80} autres lignes.</p> : null}
            </>
          )
        ) : null}
        <div className="admin-panel__btnrow">
          <button type="button" className="admin-panel__button admin-panel__button--primary" disabled={!parsed || !!parsed.fatal || changes === 0} onClick={doImport}>
            Importer
          </button>
          {raw ? <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={() => setRaw('')}>Vider</button> : null}
        </div>
      </section>
    </div>
  );
};
